import { useState, useEffect } from 'react'
import { X, FileText, Database, Info, Search, Save, Trash2, Plus } from 'lucide-react'
import { Document, Page, pdfjs } from 'react-pdf'
import { supabase } from '../../lib/supabase'
import { ErrorHandler } from '../../utils/errorHandler'
import { api } from '../../lib/api'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`

interface DocumentModalProps {
  document: any
  isOpen: boolean
  onClose: () => void
  onUpdate: () => void
}

interface Chunk {
  id: string
  content: string
  metadata: any
  embedding?: any
  created_at: string
}

export function DocumentModal({ document, isOpen, onClose, onUpdate }: DocumentModalProps) {
  const [activeTab, setActiveTab] = useState<'preview' | 'chunks' | 'metadata'>('preview')
  const [chunks, setChunks] = useState<Chunk[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [numPages, setNumPages] = useState<number | null>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [editedChunks, setEditedChunks] = useState<Map<string, string>>(new Map())
  const [hasChanges, setHasChanges] = useState(false)
  const [fileUrl, setFileUrl] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen && document) {
      loadDocumentData()
      loadFileUrl()
      // Debug: Check what's in the database
      debugCheckDatabase()
    }
  }, [isOpen, document])
  
  const debugCheckDatabase = async () => {
    try {
      // Check if we can access the table at all
      const { data: sampleChunks, error: sampleError } = await supabase
        .from('document_chunks')
        .select('id, document_id')
        .limit(5)
      
      if (sampleError) {
        console.log('Cannot access document_chunks table:', sampleError)
      } else {
        console.log('Sample chunks from database:', sampleChunks)
        console.log('Total sample chunks found:', sampleChunks?.length)
      }
      
      // Check for this specific document
      const { count, error: countError } = await supabase
        .from('document_chunks')
        .select('id', { count: 'exact', head: true })
        .eq('document_id', document.id)
      
      if (!countError) {
        console.log(`Database says document ${document.id} has ${count} chunks`)
      }
    } catch (err) {
      console.error('Debug check failed:', err)
    }
  }

  const loadFileUrl = async () => {
    if (!document?.file_path) return
    
    try {
      // Get public URL for the document
      const { data } = supabase.storage
        .from('documents')
        .getPublicUrl(document.file_path)
      
      if (data?.publicUrl) {
        setFileUrl(data.publicUrl)
      }
    } catch (error) {
      console.error('Error loading file URL:', error)
    }
  }

  const loadDocumentData = async () => {
    setLoading(true)
    try {
      // Try backend API first
      console.log('Loading chunks for document:', document.id)
      
      try {
        const response = await api.get(`/api/chunks/document/${document.id}`)
        console.log('Chunks API response:', response)
        console.log('Loaded chunks data:', response.data)
        console.log('Number of chunks:', response.data?.length || 0)
        setChunks(response.data || [])
      } catch (apiError: any) {
        console.error('API error, falling back to Supabase:', apiError)
        
        // Fallback to direct Supabase access if API fails
        console.log('Trying Supabase fallback for document:', document.id)
        
        // First, let's check what tables exist and if we have access
        const { data: chunksData, error: supabaseError } = await supabase
          .from('document_chunks')
          .select('*')
          .eq('document_id', document.id)
          .order('chunk_index', { ascending: true })
        
        if (supabaseError) {
          console.error('Supabase error:', supabaseError)
          console.error('Error details:', {
            code: supabaseError.code,
            message: supabaseError.message,
            details: supabaseError.details,
            hint: supabaseError.hint
          })
          
          // Try alternative table name
          const { data: altChunks, error: altError } = await supabase
            .from('chunks')
            .select('*')
            .eq('document_id', document.id)
          
          if (!altError && altChunks) {
            console.log('Found chunks in alternative table:', altChunks)
            setChunks(altChunks)
            return
          }
          
          throw supabaseError
        }
        
        console.log('Loaded chunks from Supabase:', chunksData)
        console.log('Number of chunks:', chunksData?.length || 0)
        
        // If no chunks found, let's check if they exist with different field name
        if (!chunksData || chunksData.length === 0) {
          console.log('No chunks found. Checking document details:', document)
          console.log('Document has chunks_count:', document.chunks_count)
        }
        
        setChunks(chunksData || [])
      }
    } catch (error: any) {
      console.error('Error loading chunks:', error)
      ErrorHandler.showError('Fehler beim Laden der Chunks')
    } finally {
      setLoading(false)
    }
  }

  const handleChunkEdit = (chunkId: string, newContent: string) => {
    const updatedMap = new Map(editedChunks)
    updatedMap.set(chunkId, newContent)
    setEditedChunks(updatedMap)
    setHasChanges(true)
  }

  const handleDeleteChunk = async (chunkId: string) => {
    if (!confirm('Möchten Sie diesen Chunk wirklich löschen?')) return

    try {
      try {
        await api.delete(`/api/chunks/${chunkId}`)
      } catch (apiError: any) {
        console.error('API delete failed, using Supabase:', apiError)
        const { error } = await supabase
          .from('document_chunks')
          .delete()
          .eq('id', chunkId)
        
        if (error) throw error
      }
      
      setChunks(chunks.filter(c => c.id !== chunkId))
      ErrorHandler.showSuccess('Chunk gelöscht')
    } catch (error) {
      ErrorHandler.showError('Fehler beim Löschen des Chunks')
      console.error('Error deleting chunk:', error)
    }
  }

  const handleAddChunk = async () => {
    const newContent = prompt('Neuen Chunk-Inhalt eingeben:')
    if (!newContent) return

    try {
      let newChunk
      
      try {
        const response = await api.post('/api/chunks/', {
          document_id: document.id,
          content: newContent,
          chunk_index: chunks.length,
          metadata: {}
        })
        newChunk = response.data
      } catch (apiError: any) {
        console.error('API add failed, using Supabase:', apiError)
        const { data, error } = await supabase
          .from('document_chunks')
          .insert({
            document_id: document.id,
            content: newContent,
            chunk_index: chunks.length,
            metadata: {}
          })
          .select()
          .single()
        
        if (error) throw error
        newChunk = data
      }

      setChunks([...chunks, newChunk])
      ErrorHandler.showSuccess('Chunk hinzugefügt')
    } catch (error) {
      ErrorHandler.showError('Fehler beim Hinzufügen des Chunks')
      console.error('Error adding chunk:', error)
    }
  }


  const handleSaveChanges = async () => {
    setSaving(true)
    try {
      // Update all edited chunks
      for (const [chunkId, newContent] of editedChunks.entries()) {
        try {
          await api.put(`/api/chunks/${chunkId}`, {
            content: newContent,
            metadata: null
          })
        } catch (apiError: any) {
          console.error('API update failed, using Supabase:', apiError)
          const { error } = await supabase
            .from('document_chunks')
            .update({ content: newContent })
            .eq('id', chunkId)
          
          if (error) throw error
        }
      }

      ErrorHandler.showSuccess('Änderungen gespeichert')
      setEditedChunks(new Map())
      setHasChanges(false)
      loadDocumentData() // Reload to get updated data
      onUpdate() // Notify parent
    } catch (error) {
      ErrorHandler.showError('Fehler beim Speichern der Änderungen')
      console.error('Error saving changes:', error)
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  const isPDF = document?.file_type === 'application/pdf' || document?.file_path?.endsWith('.pdf')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="bg-gray-900 rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {document?.title || 'Dokument'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700">
          <button
            onClick={() => setActiveTab('preview')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'preview'
                ? 'text-primary-400 border-b-2 border-primary-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Dokument
          </button>
          <button
            onClick={() => setActiveTab('chunks')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'chunks'
                ? 'text-primary-400 border-b-2 border-primary-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Database className="w-4 h-4 inline mr-2" />
            Chunks ({chunks.length})
          </button>
          <button
            onClick={() => setActiveTab('metadata')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'metadata'
                ? 'text-primary-400 border-b-2 border-primary-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Info className="w-4 h-4 inline mr-2" />
            Metadaten
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary-400" />
            </div>
          ) : (
            <>
              {/* Preview Tab */}
              {activeTab === 'preview' && (
                <div className="p-6">
                  {isPDF && fileUrl ? (
                    <div className="flex flex-col items-center">
                      <Document
                        file={fileUrl}
                        onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                        className="max-w-full"
                      >
                        <Page 
                          pageNumber={pageNumber} 
                          width={800}
                          renderTextLayer={true}
                          renderAnnotationLayer={true}
                        />
                      </Document>
                      {numPages && (
                        <div className="flex items-center gap-4 mt-4">
                          <button
                            onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                            disabled={pageNumber <= 1}
                            className="btn btn-secondary"
                          >
                            Vorherige
                          </button>
                          <span className="text-gray-400">
                            Seite {pageNumber} von {numPages}
                          </span>
                          <button
                            onClick={() => setPageNumber(Math.min(numPages, pageNumber + 1))}
                            disabled={pageNumber >= numPages}
                            className="btn btn-secondary"
                          >
                            Nächste
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-gray-800 rounded-lg p-6">
                      <pre className="whitespace-pre-wrap text-gray-300">
                        {document?.content || 'Kein Inhalt verfügbar'}
                      </pre>
                    </div>
                  )}
                </div>
              )}

              {/* Chunks Tab */}
              {activeTab === 'chunks' && (
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Dokumenten-Chunks</h3>
                    <div className="flex gap-2">
                      {hasChanges && (
                        <button
                          onClick={handleSaveChanges}
                          disabled={saving}
                          className="btn btn-primary flex items-center gap-2"
                        >
                          <Save className="w-4 h-4" />
                          {saving ? 'Speichern...' : 'Änderungen übernehmen'}
                        </button>
                      )}
                      <button
                        onClick={handleAddChunk}
                        className="btn btn-secondary flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Chunk hinzufügen
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {chunks.map((chunk, index) => (
                      <div key={chunk.id} className="bg-gray-800 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-sm text-gray-400">
                            Chunk #{index + 1} (ID: {chunk.id.slice(0, 8)}...)
                          </span>
                          <button
                            onClick={() => handleDeleteChunk(chunk.id)}
                            className="text-red-400 hover:text-red-300 p-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <textarea
                          value={editedChunks.get(chunk.id) ?? chunk.content}
                          onChange={(e) => handleChunkEdit(chunk.id, e.target.value)}
                          className="w-full bg-gray-700 text-white rounded-lg p-3 min-h-[100px] resize-y"
                          placeholder="Chunk-Inhalt..."
                        />
                        {chunk.embedding && (
                          <div className="mt-2 text-xs text-green-400">
                            ✓ Embedding vorhanden
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {chunks.length === 0 && (
                    <div className="text-center text-gray-500 py-8">
                      Keine Chunks vorhanden
                    </div>
                  )}
                </div>
              )}

              {/* Metadata Tab */}
              {activeTab === 'metadata' && (
                <div className="p-6">
                  <div className="bg-gray-800 rounded-lg p-6 space-y-4">
                    <div>
                      <label className="text-sm text-gray-400">Dokument ID</label>
                      <p className="text-white">{document?.id}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">Dateiname</label>
                      <p className="text-white">{document?.title}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">Dateityp</label>
                      <p className="text-white">{document?.file_type || 'Unbekannt'}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">Größe</label>
                      <p className="text-white">
                        {document?.file_size 
                          ? `${(document.file_size / 1024 / 1024).toFixed(2)} MB`
                          : 'Unbekannt'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">Hochgeladen am</label>
                      <p className="text-white">
                        {document?.created_at 
                          ? new Date(document.created_at).toLocaleString('de-DE')
                          : 'Unbekannt'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">Status</label>
                      <p className="text-white">{document?.extraction_status || 'Unbekannt'}</p>
                    </div>
                    {document?.metadata && (
                      <div>
                        <label className="text-sm text-gray-400">Zusätzliche Metadaten</label>
                        <pre className="text-white text-sm bg-gray-700 rounded p-3 mt-2">
                          {JSON.stringify(document.metadata, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}